/**
 * Mobile-only Bottom Tab Bar.
 *
 * Why this exists:
 *   The web app today uses a desktop-style Header + Footer chrome that doesn't
 *   feel like a native app on a phone. This component adds the canonical
 *   marketplace bottom-tab pattern (Sahibinden / Trendyol / Dubizzle / OLX style)
 *   so the experience starts to feel app-shaped without touching any page.
 *
 * Scope (deliberately tight):
 *   - Mobile + tablet-portrait: hidden at the `lg` breakpoint and above
 *     (`lg:hidden`, ≥1024px). Below that it stays visible — this matches the
 *     pattern used by Sahibinden / OLX / Trendyol where the bottom tab bar
 *     persists through phones AND iPad-portrait, and the desktop chrome only
 *     takes over at true desktop widths. Using `md:hidden` (≥768px) was the
 *     reason the bar didn't appear in narrow desktop browser previews.
 *   - 5 fixed tabs that map to ALREADY EXISTING routes in App.tsx — no new
 *     routes invented here.
 *   - Center tab is a raised, gold-gradient circular CTA (the primary
 *     "publish a listing" action), matching the visual language of the
 *     existing desktop-only `FloatingAddButton`.
 *   - Pure presentational: no business logic, no API calls, no auth checks.
 *     Auth-gated routes (e.g. /create-listing, /messages, /settings) handle
 *     their own redirects exactly as they do today on desktop.
 *
 * Route mapping rationale:
 *   - الرئيسية   → "/"               (Home — already in routes)
 *   - الأقسام    → "/categories"     (mobile-only categories landing page,
 *                                     `MobileCategories.tsx`. Was pointing
 *                                     at "/all-products" — that's a flat
 *                                     results page, not a real categories
 *                                     experience, so the tap landed users
 *                                     on the wrong surface. The new page
 *                                     reuses MobileCategoryDrillDown so the
 *                                     browse model is consistent with Home.
 *                                     Desktop visitors to /categories are
 *                                     auto-redirected back to /all-products
 *                                     by the page itself — desktop UX is
 *                                     intentionally untouched.)
 *   - إضافة     → "/create-listing" (canonical add-listing page)
 *   - الرسائل   → "/messages"        (already in routes)
 *   - حسابي     → "/settings"        (AccountSettings page)
 *
 * Safe-area:
 *   `pb-[env(safe-area-inset-bottom)]` keeps the bar above the iOS home
 *   indicator inside Capacitor / standalone PWA mode.
 *
 * Z-index:
 *   `z-40` — below toasts/dialogs (z-50) but above page content. Existing
 *   floating buttons that survive on mobile (e.g. FloatingCartButton at
 *   `bottom-20`) sit visually above this bar without needing edits.
 */

import { Link, useLocation } from "wouter";
import { Capacitor } from "@capacitor/core";
import { Home, LayoutGrid, Plus, MessageCircle, User } from "lucide-react";
import { nativeTabNavigate } from "@/lib/nativeNav";

type TabKey = "home" | "categories" | "messages" | "account";

interface SideTab {
  key: TabKey;
  href: string;
  label: string;
  Icon: typeof Home;
  testId: string;
}

const SIDE_TABS: readonly SideTab[] = [
  { key: "home",       href: "/",             label: "الرئيسية", Icon: Home,          testId: "tab-home" },
  { key: "categories", href: "/categories",   label: "الأقسام",  Icon: LayoutGrid,    testId: "tab-categories" },
  { key: "messages",   href: "/messages",     label: "الرسائل",  Icon: MessageCircle, testId: "tab-messages" },
  { key: "account",    href: "/settings",     label: "حسابي",    Icon: User,          testId: "tab-account" },
] as const;

function isActive(path: string, current: string): boolean {
  if (path === "/") return current === "/";
  return current === path || current.startsWith(`${path}/`);
}

export function BottomNav() {
  const [location] = useLocation();
  const nativeShell = Capacitor.isNativePlatform();

  return (
    <nav
      aria-label="التنقل السفلي"
      dir="rtl"
      data-testid="bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card/95 backdrop-blur border-t border-border shadow-[0_-4px_12px_-6px_rgba(0,0,0,0.08)] pb-[env(safe-area-inset-bottom)]"
    >
      <ul className="grid grid-cols-5 h-16 items-stretch">
        {/* Slot 1 + 2: first two side tabs (Home, Categories) */}
        {SIDE_TABS.slice(0, 2).map((tab) => (
          <SideTabItem key={tab.key} tab={tab} active={isActive(tab.href, location)} nativeShell={nativeShell} />
        ))}

        {/* Slot 3: raised center "Add" CTA — the primary action */}
        <li className="flex items-start justify-center">
          <Link
            href="/create-listing"
            data-testid="tab-add"
            aria-label="إضافة إعلان جديد"
            className="-mt-5 flex flex-col items-center justify-center w-14 h-14 rounded-full gradient-gold shadow-lg shadow-amber-500/40 border-2 border-card active:scale-95 transition-transform"
          >
            <Plus className="w-6 h-6 text-black" strokeWidth={2.5} />
          </Link>
        </li>

        {/* Slot 4 + 5: last two side tabs (Messages, Account) */}
        {SIDE_TABS.slice(2).map((tab) => (
          <SideTabItem key={tab.key} tab={tab} active={isActive(tab.href, location)} nativeShell={nativeShell} />
        ))}
      </ul>
    </nav>
  );
}

function SideTabItem({
  tab,
  active,
  nativeShell,
}: {
  tab: SideTab;
  active: boolean;
  nativeShell: boolean;
}) {
  const { Icon, label, href, testId } = tab;
  const className = [
    "flex-1 flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors",
    active ? "text-primary" : "text-muted-foreground hover:text-foreground",
  ].join(" ");

  if (nativeShell) {
    return (
      <li className="flex">
        <button
          type="button"
          data-testid={testId}
          aria-current={active ? "page" : undefined}
          className={className}
          onClick={() => nativeTabNavigate(href)}
        >
          <Icon className={["w-5 h-5", active ? "stroke-[2.25]" : "stroke-2"].join(" ")} />
          <span className="leading-none">{label}</span>
        </button>
      </li>
    );
  }

  return (
    <li className="flex">
      <Link
        href={href}
        data-testid={testId}
        aria-current={active ? "page" : undefined}
        className={className}
      >
        <Icon className={["w-5 h-5", active ? "stroke-[2.25]" : "stroke-2"].join(" ")} />
        <span className="leading-none">{label}</span>
      </Link>
    </li>
  );
}
